import React, { useState, useEffect } from "react";
import { DndContext, closestCorners, MouseSensor, TouchSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import CloseIcon from "components/icons/CloseIcon";
import ActionButton from "components/ActionButton";
import { postRequest } from "sharedUtils/httpUtils";

const SortableTab = ({ tab, selected, handleTabClick, onEdit, onDelete, userPostTabs }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        minWidth: "fit-content",
        whiteSpace: "nowrap",
        height: "38px",
        marginLeft: "8px",
    };

    return (
        <button
            ref={setNodeRef}
            id={`tab-${tab.id}`}
            {...attributes}
            className={`btn ${selected ? 'btn-primary' : 'btn-outline-brand'}`}
            style={style}
            onClick={() => handleTabClick(tab.id)}
        >
            <i
                {...listeners}
                className="fas fa-grip-vertical"
                style={{ cursor: "grab", display: selected ? "" : "none" }}
                title="Drag to reorder"
                onClick={(e) => e.stopPropagation()}
            />
            <span>{tab.name}</span>
            {selected && (
                <>
                    {userPostTabs?.length > 1 && (
                        <i
                            className="fas fa-trash ml-2"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(tab.id);
                            }}
                        />
                    )}
                    <i
                        className="fas fa-pen ml-2"
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(tab.id, tab.name);
                        }}
                    />
                </>
            )}
        </button>
    );
};

const UserPostTabsManager = ({
    deviceSize,
    userPostTabs,
    selectedUserPostTabId,
    loggedInUser,
    handleTabClick,
    onCreateTab,
    onEditTab,
    onDeleteTab,
    reorderUserPostTabs,
    setSelectedTab
}) => {
    const [items, setItems] = useState([...userPostTabs]);
    const [addingTab, setAddingTab] = useState(false);
    const [newTabName, setNewTabName] = useState("");
    const [editingTabId, setEditingTabId] = useState(null);
    const [tabEditName, setTabEditName] = useState("");
    const [activeTab, setActiveTab] = useState(null);

    const sensors = useSensors(
        useSensor(MouseSensor),
        useSensor(TouchSensor, { activationConstraint: { distance: 10 } })
    );

    useEffect(() => {
        setItems([...userPostTabs]);
    }, [userPostTabs]);

    const saveUserPostTabsReorder = async (reordered) => {
        await postRequest(`/api/Post/SaveUserPostTabOrdinals`, reordered);
    };

    const handleDragStart = (event) => {
        const tabId = event.active.id;
        const tabEl = document.getElementById(`tab-${tabId}`);
        if (tabEl) {
            const rect = tabEl.getBoundingClientRect();
            const computedStyles = getComputedStyle(tabEl);
            setActiveTab({
                ...items.find(t => t.id === tabId),
                width: `${rect.width}px`,
                height: `${rect.height}px`,
                fontSize: computedStyles.fontSize,
                fontWeight: computedStyles.fontWeight,
                padding: computedStyles.padding,
                border: computedStyles.border,
            });
            setSelectedTab(tabId);
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = items.findIndex(tab => tab.id === active.id);
            const newIndex = items.findIndex(tab => tab.id === over.id);
            const reordered = arrayMove(items, oldIndex, newIndex).map((tab, idx) => ({ ...tab, ordinal: idx + 1 }));
            setItems(reordered);
            await saveUserPostTabsReorder(reordered);
            reorderUserPostTabs(reordered);
        }
    };

    const handleCreate = () => {
        if (newTabName.trim()) {
            onCreateTab(newTabName.trim());
            setNewTabName("");
            setAddingTab(false);
        }
    };

    const handleEdit = () => {
        if (tabEditName.trim() && editingTabId !== null) {
            onEditTab(editingTabId, tabEditName.trim());
            setEditingTabId(null);
            setTabEditName("");
        }
    };

    return (
        <>
        <div className="row mx-3">
            <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map(tab => tab.id)}>
                    <div className={`d-flex flex-wrap justify-content-${deviceSize === "sm" ? "center" : "center"}`} style={{ gap: "10px", minHeight: "44px" }}>
                        {items.sort((a, b) => a.ordinal - b.ordinal).map(tab => (
                            <SortableTab
                                key={tab.id}
                                tab={tab}
                                selected={selectedUserPostTabId === tab.id}
                                handleTabClick={handleTabClick}
                                onEdit={(id, name) => { setEditingTabId(id); setTabEditName(name); setAddingTab(false); }}
                                onDelete={(id) => { if (items.length > 1) onDeleteTab(id); }}
                                userPostTabs={items}
                            />
                        ))}
                        <CloseIcon opacity={1} fill="#da4100" width={14} height={14} style={{ display: addingTab ? "none" : "", position: "relative", transform: addingTab ? "" : "rotate(45deg)", top: "13px", marginLeft: "5px", cursor: "pointer" }} onClick={() => { setAddingTab(true); setEditingTabId(null); }} />
                    </div>
                </SortableContext>
                <DragOverlay adjustScale={false}>
                    {activeTab && (
                        <button
                            className={`btn mx-2 ${selectedUserPostTabId === activeTab.id ? 'btn-primary' : 'btn-outline-brand'}`}
                            style={{
                                width: activeTab.width,
                                height: activeTab.height,
                                fontSize: activeTab.fontSize,
                                fontWeight: activeTab.fontWeight,
                                padding: activeTab.padding,
                                border: activeTab.border,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            <i className="fas fa-grip-vertical" style={{ cursor: "grab" }} />
                            <span>{activeTab.name}</span>
                        </button>
                    )}
                </DragOverlay>
            </DndContext>            
            </div>
            <div className="w-100 mb-3 my-4">
                {addingTab && (
                    <div className="w-100 mb-3 d-flex justify-content-center">
                        <div style={{ maxWidth: "325px", width: "100%", fontFamily: "'Urbanist',sans-serif" }}>
                            <div className="d-flex justify-content-between">
                                <label htmlFor="newTabName">New Tab</label>
                                <CloseIcon onClick={() => { setAddingTab(false); setNewTabName(""); }} style={{ cursor: "pointer" }} />
                            </div>
                            <input type="text" className="form-control mb-2 italic-placeholder" placeholder="Instagram Posts, TikTok Posts, etc" value={newTabName} onChange={(e) => setNewTabName(e.target.value)} />
                            <ActionButton className="btn btn-primary w-100" onClick={handleCreate}>Add Tab</ActionButton>
                        </div>
                    </div>
                )}

                {editingTabId !== null && (
                    <div className="w-100 d-flex justify-content-center">
                        <div style={{ maxWidth: "325px", width: "100%", fontFamily: "'Urbanist',sans-serif" }}>
                            <div className="d-flex justify-content-between">
                                <label htmlFor="tabEditName">Edit Tab</label>
                                <CloseIcon onClick={() => { setEditingTabId(null); setTabEditName(""); }} style={{ cursor: "pointer" }} />
                            </div>
                            <input type="text" className="form-control mb-2" value={tabEditName} onChange={(e) => setTabEditName(e.target.value)} />
                            <ActionButton className="btn btn-primary w-100" onClick={handleEdit}>Save Changes</ActionButton>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default UserPostTabsManager;
